import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Loader2, Eye, EyeOff, Plus, X, Film, Image } from 'lucide-react';
import adminApi from '@/lib/adminApi';

export default function AdminBlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [post, setPost] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    vimeo_id: '',
    gallery_images: [],
    tags: [],
    published: false,
  });
  const [tagInput, setTagInput] = useState('');
  const [galleryInput, setGalleryInput] = useState('');

  useEffect(() => {
    if (isEditing) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await adminApi.get(`/api/admin/blog/${id}`);
      setPost(response.data);
    } catch (error) {
      navigate('/admin/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPost((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Auto-generate slug from title
    if (name === 'title' && !isEditing) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setPost((prev) => ({ ...prev, slug }));
    }
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!post.tags.includes(tagInput.trim())) {
        setPost((prev) => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()],
        }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setPost((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleAddGalleryImage = () => {
    if (galleryInput.trim() && !post.gallery_images.includes(galleryInput.trim())) {
      setPost((prev) => ({
        ...prev,
        gallery_images: [...prev.gallery_images, galleryInput.trim()],
      }));
      setGalleryInput('');
    }
  };

  const handleRemoveGalleryImage = (imageToRemove) => {
    setPost((prev) => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((img) => img !== imageToRemove),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (isEditing) {
        await adminApi.put(`/api/admin/blog/${id}`, post);
      } else {
        await adminApi.post('/api/admin/blog', post);
      }
      navigate('/admin/blog');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-blog-editor">
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/blog')}
          className="p-2 text-[#A1A1AA] hover:text-white transition-colors"
        >
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-3xl font-bold">
          {isEditing ? 'Edit Post' : 'New Post'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <label htmlFor="title" className="form-label">Title</label>
              <input
                type="text"
                id="title"
                name="title"
                value={post.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-[#0C0C0C] border border-[#27272A] text-white focus:border-[#007AFF] focus:outline-none"
                placeholder="Post title"
                data-testid="post-title-input"
              />
            </div>

            <div>
              <label htmlFor="slug" className="form-label">Slug</label>
              <input
                type="text"
                id="slug"
                name="slug"
                value={post.slug}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-[#0C0C0C] border border-[#27272A] text-white focus:border-[#007AFF] focus:outline-none"
                placeholder="post-url-slug"
                data-testid="post-slug-input"
              />
            </div>

            <div>
              <label htmlFor="excerpt" className="form-label">Excerpt</label>
              <textarea
                id="excerpt"
                name="excerpt"
                value={post.excerpt}
                onChange={handleChange}
                required
                rows={3}
                className="w-full px-4 py-3 bg-[#0C0C0C] border border-[#27272A] text-white focus:border-[#007AFF] focus:outline-none resize-none"
                placeholder="Brief description for SEO and previews"
                data-testid="post-excerpt-input"
              />
            </div>

            <div>
              <label htmlFor="content" className="form-label">Content (HTML)</label>
              <textarea
                id="content"
                name="content"
                value={post.content}
                onChange={handleChange}
                required
                rows={15}
                className="w-full px-4 py-3 bg-[#0C0C0C] border border-[#27272A] text-white focus:border-[#007AFF] focus:outline-none resize-none font-mono text-sm"
                placeholder="<p>Your blog post content...</p>"
                data-testid="post-content-input"
              />
              <p className="mt-2 text-[#52525B] text-sm">
                Use HTML tags: &lt;p&gt;, &lt;h2&gt;, &lt;h3&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;li&gt;, etc.
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="p-6 border border-[#27272A] bg-[#0C0C0C]">
              <h3 className="font-bold mb-4">Publish</h3>
              
              <label className="flex items-center gap-3 cursor-pointer mb-6">
                <input
                  type="checkbox"
                  name="published"
                  checked={post.published}
                  onChange={handleChange}
                  className="w-5 h-5 accent-[#007AFF]"
                  data-testid="post-published-checkbox"
                />
                <span className="flex items-center gap-2">
                  {post.published ? (
                    <>
                      <Eye size={16} className="text-green-400" />
                      Published
                    </>
                  ) : (
                    <>
                      <EyeOff size={16} className="text-[#52525B]" />
                      Draft
                    </>
                  )}
                </span>
              </label>

              <button
                type="submit"
                disabled={saving}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="save-post-button"
              >
                {saving ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    {isEditing ? 'Update Post' : 'Create Post'}
                  </>
                )}
              </button>
            </div>

            <div className="p-6 border border-[#27272A] bg-[#0C0C0C]">
              <label htmlFor="featured_image" className="form-label">Featured Image URL</label>
              <input
                type="url"
                id="featured_image"
                name="featured_image"
                value={post.featured_image}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#050505] border border-[#27272A] text-white focus:border-[#007AFF] focus:outline-none text-sm"
                placeholder="https://..."
                data-testid="post-image-input"
              />
              {post.featured_image && (
                <img
                  src={post.featured_image}
                  alt="Preview"
                  className="mt-4 w-full aspect-video object-cover"
                />
              )}
            </div>

            <div className="p-6 border border-[#27272A] bg-[#0C0C0C]">
              <label htmlFor="vimeo_id" className="form-label flex items-center gap-2">
                <Film size={16} className="text-[#007AFF]" />
                Video Banner (Vimeo ID)
              </label>
              <input
                type="text"
                id="vimeo_id"
                name="vimeo_id"
                value={post.vimeo_id || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#050505] border border-[#27272A] text-white focus:border-[#007AFF] focus:outline-none text-sm"
                placeholder="e.g. 465589411"
                data-testid="post-vimeo-input"
              />
              <p className="mt-2 text-[#52525B] text-xs">
                Enter the Vimeo video ID (the number at the end of the URL)
              </p>
            </div>

            <div className="p-6 border border-[#27272A] bg-[#0C0C0C]">
              <label className="form-label flex items-center gap-2">
                <Image size={16} className="text-[#007AFF]" />
                Gallery Images
              </label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={galleryInput}
                  onChange={(e) => setGalleryInput(e.target.value)}
                  className="flex-1 px-4 py-3 bg-[#050505] border border-[#27272A] text-white focus:border-[#007AFF] focus:outline-none text-sm"
                  placeholder="Image URL"
                  data-testid="post-gallery-input"
                />
                <button
                  type="button"
                  onClick={handleAddGalleryImage}
                  className="px-3 py-2 bg-[#007AFF] text-white hover:bg-[#007AFF]/80 transition-colors"
                >
                  <Plus size={18} />
                </button>
              </div>
              {post.gallery_images && post.gallery_images.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {post.gallery_images.map((img, index) => (
                    <div key={`gallery-edit-${index}-${img.slice(-20)}`} className="relative group">
                      <img
                        src={img}
                        alt={`Gallery ${index + 1}`}
                        className="w-full aspect-square object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveGalleryImage(img)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border border-[#27272A] bg-[#0C0C0C]">
              <label className="form-label">Tags</label>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                className="w-full px-4 py-3 bg-[#050505] border border-[#27272A] text-white focus:border-[#007AFF] focus:outline-none text-sm"
                placeholder="Press Enter to add tag"
                data-testid="post-tags-input"
              />
              <div className="flex flex-wrap gap-2 mt-3">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-[#007AFF]/20 text-[#007AFF] cursor-pointer hover:bg-red-500/20 hover:text-red-400"
                    onClick={() => handleRemoveTag(tag)}
                  >
                    {tag}
                    <span className="text-xs">×</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
